import { useEffect, useState } from 'react';

import { getAdminRoute } from 'helpers/session';
import { useToast } from 'hooks/toast';
import AppPage from 'layouts/AppPage/AppPage';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { EmailVerificationAlert } from './components/EmailVerificationAlert/EmailVerificationAlert';
import { TwoFactorAuthenticationEnforcementModal } from './components/TwoFactorAuthenticationEnforcementModal/TwoFactorAuthenticationEnforcementModal';

import { messagePopOut } from 'api/app-slice';
import { logout, selectAuthentication, stopImpersonation } from 'api/authentication-slice';
import { resendUserEmailValidation, resetUser, selectUser } from 'api/user-slice';

const AccountValidation = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const showToast = useToast();

  const user = useSelector(selectUser);
  const { impersonating: isInterpreting } = useSelector(selectAuthentication);

  const [loading, setLoading] = useState(false);
  const { target } = useParams();

  const [verificationAlert, setVerificationAlert] = useState(false);
  const [emailVerificationSent, setEmailVerificationSent] = useState(false);
  const [emailVerificationError, setEmailVerificationError] = useState(null);

  const [twoFactorAuthenticationEnforcementModal, setTwoFactorAuthenticationEnforcementModal] =
    useState(false);

  useEffect(() => {
    switch (target) {
      case 'email':
        if (user.verified) {
          redirectToDashboard();
        } else {
          setVerificationAlert(true);
        }
        break;
      case '2fa':
        if (user.two_factor_auth_enabled) {
          redirectToDashboard();
        } else {
          setTwoFactorAuthenticationEnforcementModal(true);
        }
        break;
      default:
        break;
    }
  }, [target]);

  const handleStopInterpreting = async () => {
    dispatch(resetUser());
    dispatch(messagePopOut());
    try {
      await dispatch(stopImpersonation()).unwrap();
      navigate(getAdminRoute());
      window.Intercom?.('shutdown');
    } catch {
      await dispatch(logout());
      navigate('/sign_in');
    }
  };

  const handleSignOut = async () => {
    if (isInterpreting) {
      await handleStopInterpreting();
      return;
    }

    await dispatch(logout());
    navigate('/sign_in');
  };

  const resendEmail = async () => {
    if (user.verified) return;

    setLoading(true);
    try {
      await dispatch(resendUserEmailValidation(user.id)).unwrap();
      setEmailVerificationError(null);
      setEmailVerificationSent(true);
      showToast('Success', 'Verification email sent. Please check your inbox.');
    } catch (error) {
      setEmailVerificationError(
        error?.error?.log ||
          error?.error?.message ||
          'Failed to send verification email. Please try again.',
      );
    } finally {
      setLoading(false);
    }
  };

  const redirectToDashboard = () => {
    navigate('/dashboard');
  };

  return (
    <AppPage loading={loading} hideNavigation={true}>
      <EmailVerificationAlert
        visble={verificationAlert}
        resend={resendEmail}
        sent={emailVerificationSent}
        user={user}
        error={emailVerificationError}
        interpreting={isInterpreting}
        onSignOut={handleSignOut}
      />
      <TwoFactorAuthenticationEnforcementModal
        user={user}
        visble={twoFactorAuthenticationEnforcementModal}
        setLoading={setLoading}
        onSuccess={redirectToDashboard}
      />
    </AppPage>
  );
};

export default AccountValidation;
